import { api } from "shared/redux/api"
import { buildProcurementListFilters } from "./procurement"
import { BOOK_INVENTORY_TAGS } from "./procurementItems"
import { camelizeKeys } from "shared/utils/camelize"

// Procurement Manager — Approval Inbox + Order Review Detail.
//
// Unlike procurementV2 (mock-backed), these hit the REAL backend under
// `/private/procurement/v1/*` (versioned per the 2026-08-2x procurement path
// cleanup). The axios base query attaches the bearer token only when the URL
// contains "private", so every authed path keeps that prefix. If the backend
// isn't live yet the hooks surface genuine loading/error states (no mock
// fallback), per the feature brief.
const BASE = "/private/procurement/v1"

// Every response here is wrapped in an envelope (`{status/code, message, data}`)
// with the actual payload one level down under `data` — unwrap that first, then
// camelCase it. Using `camelizeKeys` directly on the envelope (as this file did
// before) leaves callers reading `result.items` when the real payload is at
// `result.data.items`, so every list silently renders empty.
const unwrap = (response) => camelizeKeys(response?.data)

// Defensive unwrap for list endpoints whose exact envelope isn't confirmed —
// accepts a bare array, an `envelope.data` array, or a `.items`/`.content`
// page, and never throws on shapes it doesn't recognize.
const unwrapList = (response) => {
  const data = unwrap(response)
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.content)) return data.content
  return []
}

// Single-order responses (create / detail / submit / resubmit / actions) nest
// the actual record under `header` + `requester`, e.g.
// `{ header: { id, code, totalAmount, status, ... }, requester: { name, ... },
//    budgetContext, vendorProposals, documents, approvalProgress, ... }`.
// Every consumer (OrderReviewContainer, OrderDetailContainer, the order form)
// reads flat fields — `order.id`, `order.orCode`, `order.total`,
// `order.requesterName` — so flatten once here instead of touching every
// caller. No-ops if a response ever arrives already flat (no `header` key).
const flattenOrderDetail = (data) => {
  if (!data || !data.header) return data
  const { header, requester = {}, budgetContext, ...rest } = data
  return {
    ...rest,
    ...header,
    id: header.id,
    orCode: header.code,
    total: header.totalAmount,
    requestDate: header.submittedAt || header.createdAt,
    requesterId: requester.id ?? requester.userId ?? header.createdBy ?? null,
    requesterName: requester.name,
    requesterEmail: requester.email,
    requesterCenter: requester.center,
    requesterDepartment: requester.department,
    budgetContext,
    categoryName: budgetContext?.categoryName,
  }
}
const unwrapOrderDetail = (response) => flattenOrderDetail(unwrap(response))

const procurementApprovalsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Approval Inbox ──────────────────────────────────────────────────────
    // GET /procurement/approvals/inbox — order requests awaiting my decision.
    getApprovalInbox: build.query({
      query: (params = {}) => ({
        url: `${BASE}/approvals/inbox`,
        params: buildProcurementListFilters(params),
      }),
      // BE emits snake_case; FE reads camelCase. Normalize once, here.
      transformResponse: unwrap,
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: "APPROVAL-INBOX", id })),
              { type: "APPROVAL-INBOX", id: "LIST" },
            ]
          : [{ type: "APPROVAL-INBOX", id: "LIST" }],
    }),

    // GET /procurement/approvals/summary — KPI counts, independent of filters.
    getApprovalSummary: build.query({
      query: () => ({ url: `${BASE}/approvals/summary` }),
      transformResponse: unwrap,
      providesTags: [{ type: "APPROVAL-INBOX", id: "SUMMARY" }],
    }),

    // ── Order Request lists ─────────────────────────────────────────────────
    // GET /order-requests/my-orders — the current user's own order requests.
    // Tag ids are prefixed with the item's `type` ("order"/"group") in case
    // this list is ever enriched the same way GET /order-requests was — order
    // ids and group order ids come from separate sequences and could collide.
    getMyOrderRequests: build.query({
      query: (params = {}) => ({
        url: `${BASE}/order-requests/my-orders`,
        params: buildProcurementListFilters(params),
      }),
      transformResponse: unwrap,
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((o) => ({ type: "ORDER-REVIEW", id: `${o.type || "order"}-${o.id}` })),
              { type: "ORDER-REVIEW", id: "MY-LIST" },
            ]
          : [{ type: "ORDER-REVIEW", id: "MY-LIST" }],
    }),

    // A2 — GET /order-requests/shared-to-my-center — orders whose lines carry
    // show_to_center for a center the viewer belongs to (center list resolved
    // at login, server-side). Same row shape/filters as the other lists.
    getOrdersSharedToMyCenter: build.query({
      query: (params = {}) => ({
        url: `${BASE}/order-requests/shared-to-my-center`,
        params: buildProcurementListFilters(params),
      }),
      transformResponse: unwrap,
      providesTags: [{ type: "ORDER-REVIEW", id: "SHARED-TO-MY-CENTER" }],
    }),

    // GET /order-requests/for-approval — orders pending on the current user
    // as approver of the current step. Same idea as the inbox but generic
    // (no KPIs) — kept for callers that don't need the summary counts.
    getOrdersForApproval: build.query({
      query: (params = {}) => ({
        url: `${BASE}/order-requests/for-approval`,
        params: buildProcurementListFilters(params),
      }),
      transformResponse: unwrap,
      providesTags: [{ type: "ORDER-REVIEW", id: "FOR-APPROVAL" }],
    }),

    // GET /order-requests — every order request, unrestricted by owner
    // (admin / procurement-manager oversight). Returns a MIXED list: each item
    // carries `type: "order" | "group"`. An "order" item is the flat shape
    // this always returned; a "group" item's status/totalAmount/
    // budgetCategoryId are derived from its child `orders[]` (included
    // inline) — see normalizeOrderOrGroupItem in ./constants for the shared
    // row-shape used by every list that renders this. Sort/paging is computed
    // service-side across both sources (same pattern as getOrdersForApproval),
    // so `page`/`size` behave as normal despite the two backing tables.
    getAllOrderRequests: build.query({
      query: (params = {}) => ({
        url: `${BASE}/order-requests`,
        params: buildProcurementListFilters(params),
      }),
      transformResponse: unwrap,
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((o) => ({ type: "ORDER-REVIEW", id: `${o.type || "order"}-${o.id}` })),
              { type: "ORDER-REVIEW", id: "ALL-LIST" },
            ]
          : [{ type: "ORDER-REVIEW", id: "ALL-LIST" }],
    }),

    // POST /procurement/order-requests — requester creates a new draft.
    // title, budgetCategoryId, totalAmount are required; centerId comes from
    // the current user's active center, everything else is optional. Optional
    // `lines` are created in the same request.
    createOrderRequest: build.mutation({
      query: (body) => ({
        url: `${BASE}/order-requests`,
        method: "POST",
        body,
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: [
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        ...BOOK_INVENTORY_TAGS,
      ],
    }),

    // PUT /procurement/order-requests/{id} — update a draft. Same body as
    // create. `lines` omitted/null leaves lines untouched; an array becomes the
    // OR's exact line set (with id = update, without = add, missing = delete),
    // all-or-nothing. BE recomputes totalAmount from lines when there are any.
    updateOrderRequest: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/order-requests/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        ...BOOK_INVENTORY_TAGS,
      ],
    }),

    // POST /procurement/order-requests/{id}/documents — multipart upload
    // (`file` + `docType`). Gated server-side to status IN (draft,
    // info_requested); 409 otherwise. Response is the full order detail with
    // the new file appended to `documents[]` — invalidating the id tag lets
    // every consumer just re-read `order.documents` off its existing query.
    uploadOrderDocument: build.mutation({
      query: ({ id, formData }) => ({
        url: `${BASE}/order-requests/${id}/documents`,
        method: "POST",
        body: formData,
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, { id }) => [{ type: "ORDER-REVIEW", id }],
    }),

    // ── Order Review Detail ─────────────────────────────────────────────────
    // GET /procurement/order-requests/{id} — full order under review.
    getOrderReview: build.query({
      query: (id) => ({ url: `${BASE}/order-requests/${id}` }),
      transformResponse: unwrapOrderDetail,
      providesTags: (_r, _e, id) => [{ type: "ORDER-REVIEW", id }],
    }),

    // POST /v1/order-requests/{id}/delivery — requester (or procurement)
    // confirms or rejects goods delivered against this approved OR. Multipart:
    //   outcome     "received" | "rejected"   (required)
    //   purchaseId  number                    (required only if the OR has >1 open PO)
    //   note        string                    (required when outcome=rejected)
    //   reasonCode  string                    (rejected only — see DELIVERY_REJECT_REASON_OPTIONS)
    //   file        biên bản / photo          (optional; stored as a GRN doc on the PO)
    //   lines       JSON string [{orderRequestLineId, receivedQty, qualityStatus?, qualityNote?}]
    //               — records a GRN (missing/excess per item) in the same request
    //   notifyTargets "vendor,pro" | "pro" (default pro) — who is emailed on a problem
    // May be called several times for one PO (partial deliveries); the latest
    // call decides delivery_status.
    // Backend traces the OR's open PO, updates PO.delivery_status, and on
    // "received" nudges PO.status issued → receiving. Returns the refreshed OR
    // detail. Common errors: 409 (OR not approved / no open PO), 400 (multiple
    // open POs → purchaseId required, or missing note on reject), 403 (not the
    // requester or procurement).
    confirmOrderDelivery: build.mutation({
      query: ({ id, outcome, purchaseId, note, reasonCode, file, lines, notifyTargets }) => {
        const formData = new FormData()
        formData.append("outcome", outcome)
        if (purchaseId) formData.append("purchaseId", purchaseId)
        if (note) formData.append("note", note)
        if (reasonCode) formData.append("reasonCode", reasonCode)
        if (file) formData.append("file", file)
        if (lines?.length) formData.append("lines", JSON.stringify(lines))
        if (notifyTargets) formData.append("notifyTargets", notifyTargets)
        return { url: `${BASE}/order-requests/${id}/delivery`, method: "POST", body: formData }
      },
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, { id, purchaseId }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "PURCHASE", id: "LIST" },
        ...BOOK_INVENTORY_TAGS,
        ...(purchaseId ? [{ type: "PURCHASE", id: purchaseId }, { type: "GRN", id: purchaseId }] : []),
      ],
    }),

    // POST /procurement/order-requests/{id}/actions — approve / reject / info.
    // `deadline` is only meaningful for info_request: optional, "yyyy-MM-dd"
    // (BE defaults to end of day VN time) or "yyyy-MM-ddTHH:mm" for an exact
    // cutoff. Omitted entirely when not set (undefined body fields are
    // dropped by axios, so other actions are unaffected).
    // On success the caller refetches the detail; we also drop the inbox list so
    // the decided order leaves the queue.
    submitOrderAction: build.mutation({
      query: ({ id, action, note, deadline }) => ({
        url: `${BASE}/order-requests/${id}/actions`,
        method: "POST",
        body: { action, note, deadline },
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "FOR-APPROVAL" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "APPROVAL-INBOX", id: "LIST" },
        { type: "APPROVAL-INBOX", id: "SUMMARY" },
      ],
    }),

    // POST /procurement/order-requests/{id}/submit — owner submits a draft.
    submitOrderRequest: build.mutation({
      query: (id) => ({
        url: `${BASE}/order-requests/${id}/submit`,
        method: "POST",
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, id) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "FOR-APPROVAL" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "APPROVAL-INBOX", id: "LIST" },
        { type: "APPROVAL-INBOX", id: "SUMMARY" },
      ],
    }),

    // POST /procurement/order-requests/{id}/resubmit — owner resubmits after
    // an info_request, keeping the current step (rule is locked at submit).
    // POST /order-requests/{id}/close — `note` required. Only when
    // allowedActions has "close"; 409 if an active PO still hangs off it.
    closeOrderRequest: build.mutation({
      query: ({ id, note }) => ({
        url: `${BASE}/order-requests/${id}/close`,
        method: "POST",
        body: { note },
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "FOR-APPROVAL" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "APPROVAL-INBOX", id: "LIST" },
        { type: "APPROVAL-INBOX", id: "SUMMARY" },
      ],
    }),
    resubmitOrderRequest: build.mutation({
      query: ({ id, note }) => ({
        url: `${BASE}/order-requests/${id}/resubmit`,
        method: "POST",
        body: { note },
      }),
      transformResponse: unwrapOrderDetail,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "FOR-APPROVAL" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "APPROVAL-INBOX", id: "LIST" },
        { type: "APPROVAL-INBOX", id: "SUMMARY" },
      ],
    }),

    // GET /procurement/approval/preview — same resolver as the admin preview,
    // exposed under OrderRequestController for the requester-facing form/detail.
    // POST /order-requests/budget-preview — read-only: how the unsaved form's
    // lines split across Order-for months and budget buckets. `lineIndex` in
    // each row points back into the request's `lines`.
    previewOrderBudget: build.query({
      query: (body) => ({ url: `${BASE}/order-requests/budget-preview`, method: "POST", body }),
      transformResponse: unwrap,
    }),
    previewOrderApproval: build.query({
      query: (ctx = {}) => ({
        url: `${BASE}/approval/preview`,
        params: {
          entity: ctx.entity || undefined,
          category: ctx.category || undefined,
          amount: ctx.amount || undefined,
          region: ctx.region || undefined,
          center: ctx.center || undefined,
          department: ctx.department || undefined,
          urgency: ctx.urgency || undefined,
        },
      }),
      transformResponse: unwrap,
    }),

    // ── Group Orders ─────────────────────────────────────────────────────────
    // A group order bundles several of the requester's own order requests
    // under one header. Unlike a single order request, there's no draft state
    // — POST creates it already `pending` (auto-submitted) and it can't be
    // edited afterward, so there's no update/submit endpoint to call.
    // POST /procurement/group-orders — body: { title, region, urgency,
    // orderRequestIds: number[] }. Response is flat (no `header` nesting):
    // { id, code, title, region, urgency, status, createdBy, createdAt,
    //   orders: [{ id, code, title, status, totalAmount }] }.
    // 400 if orderRequestIds is empty or contains an unknown id.
    createGroupOrder: build.mutation({
      query: (body) => ({
        url: `${BASE}/group-orders`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: [
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
      ],
    }),

    // GET /procurement/group-orders/{id} — same flat shape as create; status
    // is recalculated live on every call. 404 if it doesn't exist.
    getGroupOrder: build.query({
      query: (id) => ({ url: `${BASE}/group-orders/${id}` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "GROUP-ORDER", id }],
    }),

    // GET /v1/group-orders/unclosed — group orders still open to attach a new
    // order request to (status draft/pending/approved, i.e. not yet closed).
    // Feeds the "attach to existing group" picker on the Order Request create
    // form — the create call then sends `groupOrderId` pointing at one of
    // these instead of `newGroupOrder`.
    getUnclosedGroupOrders: build.query({
      query: () => ({ url: `${BASE}/group-orders/unclosed` }),
      transformResponse: unwrapList,
      providesTags: [{ type: "GROUP-ORDER", id: "UNCLOSED-LIST" }],
    }),

    // POST /v1/group-orders/{id}/partial-approve — no body. Splits every
    // member order that isn't `approved` out of the group into a standalone
    // order (it keeps going through its own approval flow); orders already
    // `approved` stay in the group. This does NOT approve anything itself —
    // it only acts on each member's current status. Response is the group
    // detail shape (same as getGroupOrder) plus `detachedOrders: [...]` for
    // the orders that were just split off (empty array if the group was
    // already fully approved — a no-op, still 200). 409 if the group has no
    // approved member, or if it's already closed.
    partialApproveGroupOrder: build.mutation({
      query: (id) => ({ url: `${BASE}/group-orders/${id}/partial-approve`, method: "POST" }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, id) => [
        { type: "GROUP-ORDER", id },
        { type: "GROUP-ORDER", id: "UNCLOSED-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "FOR-APPROVAL" },
      ],
    }),

    // ── Order Request Lines ────────────────────────────────────────────────────
    // POST /order-requests/{id}/lines — add a line item to an order request.
    // Request: { itemId, centerId, requestType, replacedAssetCode?, quantity, unitPrice, deliverContactName?, deliverContactPhone?, deliverAddress? (≤500), note? }
    createOrderRequestLine: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${BASE}/order-requests/${id}/lines`,
        method: "POST",
        body,
      }),
      transformResponse: unwrap,
      // Also invalidates the list tags: a line add/edit/delete changes the
      // order's totalAmount, which every list row shows — without these, a
      // list opened before this edit keeps showing the stale total until its
      // cache naturally expires (keepUnusedDataFor, default 60s).
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
      ],
    }),

    // PUT /order-requests/{id}/lines/{lineId} — update a line item. Full
    // overwrite: omitting deliverAddress (or contact fields) nulls the stored value.
    updateOrderRequestLine: build.mutation({
      query: ({ id, lineId, ...body }) => ({
        url: `${BASE}/order-requests/${id}/lines/${lineId}`,
        method: "PUT",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
      ],
    }),

    // DELETE /order-requests/{id}/lines/{lineId} — delete a line item.
    deleteOrderRequestLine: build.mutation({
      query: ({ id, lineId }) => ({
        url: `${BASE}/order-requests/${id}/lines/${lineId}`,
        method: "DELETE",
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ORDER-REVIEW", id },
        { type: "ORDER-REVIEW", id: "MY-LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
      ],
    }),

    // ── Permissions gate ────────────────────────────────────────────────────
    // GET /me/permissions — capability flags gating the manager surface.
    getMyPermissions: build.query({
      query: () => ({ url: `/private/me/permissions` }),
      transformResponse: unwrap,
      providesTags: [{ type: "ME-PERMISSIONS", id: "SELF" }],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetApprovalInboxQuery,
  useGetApprovalSummaryQuery,
  useGetMyOrderRequestsQuery,
  useGetOrdersSharedToMyCenterQuery,
  useGetOrdersForApprovalQuery,
  useGetAllOrderRequestsQuery,
  useCreateOrderRequestMutation,
  useUpdateOrderRequestMutation,
  useUploadOrderDocumentMutation,
  useCreateOrderRequestLineMutation,
  useUpdateOrderRequestLineMutation,
  useDeleteOrderRequestLineMutation,
  useGetOrderReviewQuery,
  useLazyGetOrderReviewQuery,
  useSubmitOrderActionMutation,
  useConfirmOrderDeliveryMutation,
  useSubmitOrderRequestMutation,
  useResubmitOrderRequestMutation,
  useCloseOrderRequestMutation,
  usePreviewOrderApprovalQuery,
  usePreviewOrderBudgetQuery,
  useGetMyPermissionsQuery,
  useCreateGroupOrderMutation,
  useGetGroupOrderQuery,
  useLazyGetGroupOrderQuery,
  useGetUnclosedGroupOrdersQuery,
  usePartialApproveGroupOrderMutation,
} = procurementApprovalsApi

export default procurementApprovalsApi
